/**
 * The portal's data shapes.
 *
 * Field names and nesting match RinNET's `src/lib/models.ts` exactly. Anything renamed
 * here is a field the frontend silently reads as `undefined`.
 */

export interface Role {
  id: number;
  /** The literal string the frontend matches on for admin routes. */
  name: string;
}

/** The role name that gates `/admin`. Not an enum: the frontend compares this string. */
export const ROLE_ADMIN = 'ROLE_ADMIN';

export interface CardExternal {
  id: number;
  luid: string;
}

export interface Card {
  id: number;
  /** The game-facing player id. */
  extId: number;
  /** The 20-digit access code. Named `luid` here for historical reasons. */
  luid: string;
  registerTime: string;
  accessTime: string;
  cardExternalList: CardExternal[];
  /** Whether this is the account's default card. */
  default: boolean;
}

export interface SimpleUser {
  id: number;
  name: string;
}

export interface Keychip {
  id: number;
  keychipId: string;
  placeName: string;
  whiteListed: boolean;
  user: SimpleUser;
}

export interface UserTrustKeychip {
  id: number;
  userId: number;
  keychip: Keychip;
}

export interface OAuth2 {
  id: number;
  provider: string;
  email: string;
}

export interface User {
  id: number;
  username: string;
  name: string;
  email: string;
  roles: Role[];
  cards: Card[];
  defaultCard: Card | null;
  keychips: Keychip[];
  userTrustKeychips: UserTrustKeychip[];
  /**
   * Which games this account has a profile in.
   *
   * The keys are `chusan`, `maimai2` and `ongeki` — NOT the route prefixes. The
   * frontend's menu matches `user.games.includes(game)` against those exact strings
   * while its routes live under `/chuni/v2`, and reconciling the two would break it.
   */
  games: string[];
  oauth2s: OAuth2[];
  /**
   * Whether the account is shut out, and why.
   *
   * `/api/user/me` is reachable while banned or owing a password change — it has to
   * be, or the pages that explain those states could not load. That makes it the only
   * request such an account is guaranteed to complete, so it is the only place the
   * frontend can learn about the state *before* running into a 403.
   */
  restriction: Restriction | null;
}

/** Why the server will refuse this account's other requests. */
export type Restriction = 'banned' | 'mustChangePassword';

/** Menu keys, which differ from the route prefixes for CHUNITHM. */
export const GAME_KEYS = { chuni: 'chusan', mai2: 'maimai2', ongeki: 'ongeki' } as const;

/** Route prefixes, as the frontend mounts them. */
export const GAME_ROUTES = { chuni: 'chuni/v2', mai2: 'maimai2', ongeki: 'ongeki' } as const;

export interface LoginResult {
  accessToken: string;
  refreshToken: string;
  tokenType: 'Bearer';
  /** Present only when a second factor is required; no tokens are issued yet. */
  totpToken?: string;
}

export interface RefreshResult {
  accessToken: string;
}

/**
 * What `restoreAccess` checks before letting the user past the landing page.
 *
 * Served from `GET /api/account/status`. `appeal` is free text the banned page shows
 * as "how to contact us"; RinNET defaults it to a QQ group, so a null here would put
 * an empty contact line on the one page that exists to give the user a next step.
 */
export interface AccessStatus {
  banned: boolean;
  eulaRequired: boolean;
  currentEulaVersion?: number;
  acceptedEulaVersion: number | null;
  appeal: string;
}

/** `GET /api/eula/current`. `content` is rendered as HTML by the frontend. */
export interface EulaDocument {
  id: number;
  version: number;
  title: string;
  content: string;
  publishedAt: string;
  draft?: boolean;
}

export type AnnouncementStatus = 'ACTIVE' | 'EXPIRED' | 'DRAFT';
export type AnnouncementType =
  | 'GENERAL'
  | 'MAINTENANCE'
  | 'UPDATE'
  | 'EVENT'
  | 'TUTORIAL'
  | 'OTHER';

export interface LocalAnnouncement {
  language: string;
  translatedTitle: string;
  translatedContent: string;
}

export interface Announcement {
  id: number;
  title: string;
  content: string;
  expirationDate: string;
  updatedAt: string;
  status: AnnouncementStatus;
  type: AnnouncementType;
  priority: number;
  translations: LocalAnnouncement[];
}

/** The per-game summary the portal's profile page renders. */
export interface GameProfileSummary {
  userName: string;
  level: number;
  playerRating: number;
  highestRating: number;
  playCount: number;
  lastPlayDate: string | null;
  /** Rank on this game's leaderboard, or null when the player is not ranked. */
  serverRank: number | null;
  totalPlayers: number;
}

export interface RankingEntry {
  rank: number;
  cardExtId: number;
  userName: string;
  playerRating: number;
  lastPlayDate: string | null;
}

/**
 * One row of the admin player search.
 *
 * The user is nested under `user` rather than spread, which is the shape RinNET's
 * portal expects and the reason this is typed at all: reading the entry as if it were
 * the user yields `undefined` for every field, and the first `.roles.some(...)` takes
 * the whole page down with it.
 */
export interface AdminSearchEntry {
  user: User;
  /** ISO timestamp, or null when the account is in good standing. */
  bannedAt: string | null;
  banReason: string | null;
}
